import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  Alert,
  Divider,
  Chip,
  Button,
  TextField,
  CircularProgress,
  Switch,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import {
  AccountCircle as AccountIcon,
  Security as SecurityIcon,
  Api as ApiIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';

import BtraderApiSetup from '../components/security/BtraderApiSetup';
import TwoFactorSetup from '../components/security/TwoFactorSetup';
import btraderService from '../services/btraderService';
import { apiFetch } from '../utils/apiFetch';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface NotificationPrefs {
  tradeExecuted: boolean;
  stopLossAlert: boolean;
  strategySignal: boolean;
  newLogin: boolean;
  passwordChanged: boolean;
}

const defaultNotifications: NotificationPrefs = {
  tradeExecuted: true,
  stopLossAlert: true,
  strategySignal: false,
  newLogin: true,
  passwordChanged: true,
};

const SettingsPage: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(1);
  const [isApiConnected, setIsApiConnected] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [notifications, setNotifications] = useState<NotificationPrefs>(defaultNotifications);
  const [optionsLevel, setOptionsLevel] = useState<number>(0);
  const [optionsLevelSaving, setOptionsLevelSaving] = useState(false);

  const PREF_KEY_MAP: Record<keyof NotificationPrefs, string> = {
    tradeExecuted: 'notify_trade',
    stopLossAlert: 'notify_stop_loss',
    strategySignal: 'notify_strategy_signal',
    newLogin: 'notify_login',
    passwordChanged: 'notify_take_profit',
  };

  const toggleNotification = async (key: keyof NotificationPrefs) => {
    const newVal = !notifications[key];
    setNotifications(prev => ({ ...prev, [key]: newVal }));
    try {
      await apiFetch('/users/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [PREF_KEY_MAP[key]]: newVal }),
      });
    } catch {
      setNotifications(prev => ({ ...prev, [key]: !newVal }));
    }
  };

  useEffect(() => {
    checkApiConnection();
    // Load notification prefs from server
    apiFetch('/users/me').then(r => r.json()).then(user => {
      setNotifications({
        tradeExecuted: user.notify_trade ?? true,
        stopLossAlert: user.notify_stop_loss ?? true,
        strategySignal: user.notify_strategy_signal ?? false,
        newLogin: user.notify_login ?? true,
        passwordChanged: user.notify_take_profit ?? true,
      });
      setOptionsLevel(Number(user.ibkr_options_level ?? 0));
    }).catch(() => {});
  }, []);

  const handleOptionsLevelChange = async (newLevel: number) => {
    const prev = optionsLevel;
    setOptionsLevel(newLevel);
    setOptionsLevelSaving(true);
    try {
      await apiFetch('/users/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ibkr_options_level: newLevel }),
      });
    } catch {
      setOptionsLevel(prev);
    } finally {
      setOptionsLevelSaving(false);
    }
  };

  const checkApiConnection = async () => {
    setIsCheckingConnection(true);
    try {
      const connected = await btraderService.checkConnection();
      setIsApiConnected(connected);
    } catch (error) {
      console.error('Failed to check API connection:', error);
      setIsApiConnected(false);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const stored = localStorage.getItem('authTokens');
      const token = stored ? JSON.parse(stored).access_token : null;
      const res = await fetch(`${process.env.REACT_APP_API_URL || '/api/v1'}/users/me/delete-account`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: deletePassword, confirm: deleteConfirm }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Löschen fehlgeschlagen');
      }
      localStorage.removeItem('authTokens');
      setDeletePassword('');
      setDeleteConfirm('');
      window.location.href = '/login';
    } catch (e: any) {
      setDeleteError(e.message || 'Fehler beim Löschen');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleApiVerificationComplete = (verified: boolean) => {
    setIsApiConnected(verified);
    if (verified) {
      // Show success message or redirect to trading
    }
  };

  return (
    <>
      <Helmet>
        <title>Einstellungen - btrader</title>
      </Helmet>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Einstellungen
        </Typography>

        {/* Status Alert */}
        {!isCheckingConnection && (
          <Alert
            severity={isApiConnected ? 'success' : 'warning'}
            sx={{ mb: 3 }}
          >
            {isApiConnected
              ? 'BTRADER API ist verbunden und einsatzbereit für Trading'
              : 'BTRADER API nicht verbunden. Bitte konfigurieren Sie Ihre API-Keys.'
            }
          </Alert>
        )}

        <Paper sx={{ width: '100%' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="settings tabs"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={<AccountIcon />}
              label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>Account</Box>}
              id="settings-tab-0"
              aria-controls="settings-tabpanel-0"
            />
            <Tab
              icon={<ApiIcon />}
              label="BTRADER API"
              id="settings-tab-1"
              aria-controls="settings-tabpanel-1"
            />
            <Tab
              icon={<SecurityIcon />}
              label="Sicherheit"
              id="settings-tab-2"
              aria-controls="settings-tabpanel-2"
            />
            <Tab
              icon={<NotificationsIcon />}
              label="Benachrichtigungen"
              id="settings-tab-3"
              aria-controls="settings-tabpanel-3"
            />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* Account Settings */}
            <TabPanel value={currentTab} index={0}>
              <Typography variant="h5" gutterBottom>
                Account-Einstellungen
              </Typography>

              <Card sx={{ mt: 2, mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>IBKR Options-Trading-Level</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Trage hier dein in Interactive Brokers freigeschaltetes Options-Level ein.
                    Der Bot bietet nur Strategien an, die zu deinem Level passen.
                    Falsche Eingaben fuehren zu abgelehnten Orders bei IBKR.
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ maxWidth: 480 }}>
                    <InputLabel id="options-level-label">Options-Level</InputLabel>
                    <Select
                      labelId="options-level-label"
                      label="Options-Level"
                      value={optionsLevel}
                      onChange={(e) => handleOptionsLevelChange(Number(e.target.value))}
                      disabled={optionsLevelSaving}
                    >
                      <MenuItem value={0}>0 — keine Optionen</MenuItem>
                      <MenuItem value={1}>1 — Long Calls / Long Puts</MenuItem>
                      <MenuItem value={2}>2 — Covered Calls</MenuItem>
                      <MenuItem value={3}>3 — Spreads (Vertical, Iron Condor, Butterfly)</MenuItem>
                      <MenuItem value={4}>4 — Naked Calls/Puts (Cash-Secured Puts, Short Strangles)</MenuItem>
                    </Select>
                  </FormControl>
                  {optionsLevel >= 4 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Level 4 erlaubt Naked Options. Naked Calls haben theoretisch unbegrenztes
                      Verlustrisiko. Auto-Trading ist fuer Naked-Strategien standardmaessig aus —
                      Setups muessen manuell bestaetigt werden.
                    </Alert>
                  )}
                </CardContent>
              </Card>

              <Box sx={{ mt: 4, p: 3, border: '2px solid', borderColor: 'error.main', borderRadius: 2 }}>
                <Typography variant="h6" color="error" gutterBottom>
                  Gefahrenzone
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Das Löschen deines Kontos ist unwiderruflich. Alle deine Daten werden permanent gelöscht:
                  Trades, Strategien, API-Keys, Paper-Trading-Konto und persönliche Daten.
                </Typography>

                {!showDeleteConfirm ? (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Konto unwiderruflich löschen
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
                    <Alert severity="error">
                      Tippe dein Passwort ein und bestätige mit "LOESCHEN" um dein Konto zu löschen.
                    </Alert>
                    <TextField
                      label="Passwort"
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label='Tippe "LOESCHEN" zur Bestätigung'
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      fullWidth
                      size="small"
                    />
                    {deleteError && <Alert severity="error">{deleteError}</Alert>}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="error"
                        disabled={deleteConfirm !== 'LOESCHEN' || !deletePassword || deleteLoading}
                        onClick={handleDeleteAccount}
                      >
                        {deleteLoading ? <CircularProgress size={20} /> : 'Endgültig löschen'}
                      </Button>
                      <Button onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); setDeleteConfirm(''); setDeleteError(null); }}>
                        Abbrechen
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* BTRADER API */}
            <TabPanel value={currentTab} index={1}>
              <BtraderApiSetup
                onVerificationComplete={handleApiVerificationComplete}
                isConnected={isApiConnected}
              />
            </TabPanel>

            {/* Security Settings */}
            <TabPanel value={currentTab} index={2}>
              <Typography variant="h5" gutterBottom>
                Sicherheitseinstellungen
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Verwalten Sie Ihre Sicherheitseinstellungen und aktivieren Sie zusätzliche Schutzmaßnahmen.
              </Typography>

              <Box mb={4}>
                <TwoFactorSetup />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Alert severity="info">
                Weitere Sicherheitseinstellungen werden in zukünftigen Versionen hinzugefügt.
              </Alert>
            </TabPanel>

            {/* Notifications */}
            <TabPanel value={currentTab} index={3}>
              <Typography variant="h5" gutterBottom>
                Benachrichtigungen
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Konfiguriere, wie du über Trading-Aktivitäten informiert wirst.
              </Typography>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Trading-Benachrichtigungen
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2">Trade ausgeführt</Typography>
                        <Typography variant="caption" color="text.secondary">Benachrichtigung bei jedem ausgeführten Trade</Typography>
                      </Box>
                      <Switch checked={notifications.tradeExecuted} onChange={() => toggleNotification('tradeExecuted')} />
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2">Stop-Loss / Take-Profit</Typography>
                        <Typography variant="caption" color="text.secondary">Warnung wenn ein Stop-Loss oder Take-Profit ausgeloest wird</Typography>
                      </Box>
                      <Switch checked={notifications.stopLossAlert} onChange={() => toggleNotification('stopLossAlert')} />
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2">Strategie-Signale</Typography>
                        <Typography variant="caption" color="text.secondary">Info wenn eine Strategie ein Kauf- oder Verkaufssignal erkennt</Typography>
                      </Box>
                      <Switch checked={notifications.strategySignal} onChange={() => toggleNotification('strategySignal')} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Sicherheit
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2">Neuer Login</Typography>
                        <Typography variant="caption" color="text.secondary">E-Mail bei Login von einem neuen Geraet</Typography>
                      </Box>
                      <Switch checked={notifications.newLogin} onChange={() => toggleNotification('newLogin')} />
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2">Passwort geaendert</Typography>
                        <Typography variant="caption" color="text.secondary">Bestaetigung bei Passwortaenderung</Typography>
                      </Box>
                      <Switch checked={notifications.passwordChanged} onChange={() => toggleNotification('passwordChanged')} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Einstellungen werden lokal gespeichert. E-Mail-Benachrichtigungen erfordern eine konfigurierte E-Mail-Adresse.
              </Typography>
            </TabPanel>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default SettingsPage;